import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Grid, 
  Avatar, 
  Chip, 
  Box, 
  TextField, 
  IconButton,
  Select,
  MenuItem,
  Collapse,
  Paper,
  Stack,
  Modal
} from "@mui/material";
import { 
  Add as AddIcon, 
  Close as CloseIcon, 
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as PriceIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useExpertService } from "../Hooks/useExpertService";
import SoftBox from "Common/hooks/StyleFeatures/SoftBox";
import SoftButton from "Common/hooks/StyleFeatures/SoftButton";
import SoftTypography from "Common/hooks/StyleFeatures/SoftTypography";

const ExpertServices = () => {
  const navigate = useNavigate();
  const { 
    experts, 
    services, 
    loading, 
    error, 
    createService, 
    updateService, 
    addPricing 
  } = useExpertService();
  
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedServices, setExpandedServices] = useState({});
  const [newService, setNewService] = useState({
    title: "",
    description: "",
    defaultPricing: {
      price: 0,
      durationMinutes: 60,
      notes: "",
    },
  });
  const [newPricing, setNewPricing] = useState({
    expertServiceId: 0,
    price: 0,
    durationMinutes: 60,
    notes: "",
  });

  const handleOpenServiceForm = (service = null) => {
    if (service) {
      // Navigate to edit page
      navigate(`/proAdmin-dashboard/services/edit/${service.id}`);
    } else {
      // Navigate to create page
      console.log("navigating to ", )
      navigate("/proAdmin-dashboard/services/create");
    }
  };

  const handleCloseServiceForm = () => {
    setShowServiceForm(false);
    setIsEditMode(false);
  };

  const handleOpenPricingModal = (serviceId) => {
    // Find the service to get its existing pricing
    const service = services.find(s => s.id === serviceId);
    
    // Calculate default values based on existing pricing
    let defaultPrice = 100; // Default price
    let defaultDuration = 60; // Default duration in minutes
    
    if (service && service.pricings && service.pricings.length > 0) {
      // Check if we have 1h/$100 and 2h/$200 pricing
      const oneHourPricing = service.pricings.find(p => p.durationMinutes === 60);
      const twoHourPricing = service.pricings.find(p => p.durationMinutes === 120);
      
      if (oneHourPricing && twoHourPricing) {
        // Use the pattern to calculate the 3rd pricing option
        defaultPrice = oneHourPricing.price * 3; // 3x the 1-hour price
        defaultDuration = 180; // 3 hours
      } else if (service.pricings.length === 1) {
        // If only one pricing exists, use it as a base
        defaultPrice = service.pricings[0].price * 1.5; // 1.5x the existing price
        defaultDuration = service.pricings[0].durationMinutes * 1.5; // 1.5x the duration
      }
    }
    
    setNewPricing({
      expertServiceId: serviceId,
      price: defaultPrice,
      durationMinutes: defaultDuration,
      notes: "",
    });
    setPricingModalOpen(true);
  };

  const handleClosePricingModal = () => {
    setPricingModalOpen(false);
    setNewPricing({
      expertServiceId: 0,
      price: 0,
      durationMinutes: 60,
      notes: "",
    });
  };

  const handleUpdateService = async () => {
    try {
      const serviceData = {
        title: newService.title,
        description: newService.description,
      };
      await updateService(selectedService.id, serviceData);
      handleCloseServiceForm();
    } catch (error) {
      console.error("Error updating service:", error);
    }
  };

  const handleAddPricing = async () => {
    try {
      await addPricing(newPricing);
      handleClosePricingModal();
    } catch (error) {
      console.error("Error adding pricing:", error);
    }
  };

  const toggleServiceExpansion = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  if (loading) return <div>Loading experts...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <SoftBox py={3}>
      <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="medium">
            Expert Services Management
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Manage expert services and pricing options
          </SoftTypography>
        </SoftBox>
        <SoftButton
          variant="gradient"
          color="info"
          startIcon={<AddIcon />}
          onClick={() => handleOpenServiceForm()}
        >
          Add New Service
        </SoftButton>
      </SoftBox>

      <Grid container spacing={3}>
        {services && services.map((service) => (
          <Grid item xs={12} md={6} lg={4} key={service.id}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", boxShadow: 3 }}>
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                {/* Expert Info Header */}
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    src={service.expert?.profileImage}
                    alt={`${service.expert?.firstName || ''} ${service.expert?.lastName || ''}`}
                    sx={{ width: 40, height: 40, mr: 1.5 }}
                  />
                  <Box>
                    <Typography variant="h6" component="h2">
                      {service.expert?.firstName} {service.expert?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.expert?.expertCode} • {service.expert?.yearsOfExperience || 0} years exp.
                    </Typography>
                  </Box>
                </Box>

                {/* Service Title */}
                <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                  {service.title}
                </Typography>
                
                {/* Service Description - Truncated */}
                <Typography variant="body2" color="text.secondary" mb={2} noWrap>
                  {service.description}
                </Typography>

                {/* Pricing Display */}
                {service.pricings && service.pricings.length > 0 && (
                  <Box mb={2}>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      onClick={() => toggleServiceExpansion(service.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Chip 
                        icon={<PriceIcon />} 
                        label={`$${service.pricings[0]?.price || 0} for ${formatDuration(service.pricings[0]?.durationMinutes)}`}
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      {service.pricings.length > 1 && (
                        <Chip 
                          label={`+${service.pricings.length - 1} more`}
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                      <ExpandMoreIcon 
                        fontSize="small" 
                        sx={{ ml: 1, transform: expandedServices[service.id] ? 'rotate(180deg)' : 'none' }}
                      />
                    </Box>
                    
                    {/* Collapsible All Pricing Details */}
                    <Collapse in={expandedServices[service.id]} timeout="auto" unmountOnExit>
                      <Paper elevation={1} sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          All Pricing Options:
                        </Typography>
                        <Stack spacing={1}>
                          {service.pricings.map((pricing) => (
                            <Box key={pricing.id} display="flex" justifyContent="space-between" alignItems="center">
                              <Box display="flex" alignItems="center">
                                <PriceIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2">
                                  ${pricing.price} for {formatDuration(pricing.durationMinutes)}
                                </Typography>
                              </Box>
                              {pricing.isDefault && (
                                <Chip label="Default" size="small" color="primary" />
                              )}
                            </Box>
                          ))}
                        </Stack>
                        {service.pricings[0]?.notes && (
                          <Typography variant="caption" color="text.secondary" mt={1}>
                            Notes: {service.pricings[0].notes}
                          </Typography>
                        )}
                      </Paper>
                    </Collapse>
                  </Box>
                )}

                {/* Service Status and Actions */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip 
                    label={service.isActive ? "Active" : "Inactive"} 
                    size="small" 
                    color={service.isActive ? "success" : "default"} 
                  />
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPricingModal(service.id)}
                      title="Add Pricing"
                    >
                      <PriceIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenServiceForm(service)}
                      title="Edit Service"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Service Form (Inline) */}
      {showServiceForm && isEditMode && (
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }} elevation={3}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              Edit Service
            </Typography>
            <IconButton onClick={handleCloseServiceForm}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Service Fields */}
          <TextField
            fullWidth
            label="Service Title"
            margin="normal"
            value={newService.title}
            onChange={(e) =>
              setNewService({ ...newService, title: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={newService.description}
            onChange={(e) =>
              setNewService({ ...newService, description: e.target.value })
            }
          />

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={handleCloseServiceForm} sx={{ mr: 1 }}>
              Cancel
            </Button>

            <SoftButton
              variant="gradient"
              color="info"
              onClick={handleUpdateService}
            >
              Update Service
            </SoftButton>
          </Box>
        </Paper>
      )}

      {/* Add Pricing Modal */}
      <Modal open={pricingModalOpen} onClose={handleClosePricingModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Add Additional Pricing</Typography>
            <IconButton onClick={handleClosePricingModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Price ($)"
            type="number"
            margin="normal"
            value={newPricing.price}
            onChange={(e) =>
              setNewPricing({ ...newPricing, price: parseFloat(e.target.value) || 0 })
            }
          />
          <TextField
            fullWidth
            label="Duration (minutes)"
            type="number"
            margin="normal"
            value={newPricing.durationMinutes}
            onChange={(e) =>
              setNewPricing({ ...newPricing, durationMinutes: parseInt(e.target.value) || 60 })
            }
          />
          <TextField
            fullWidth
            label="Notes"
            margin="normal"
            value={newPricing.notes}
            onChange={(e) => setNewPricing({ ...newPricing, notes: e.target.value })}
          />

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={handleClosePricingModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <SoftButton variant="gradient" color="info" onClick={handleAddPricing}>
              Add Pricing
            </SoftButton>
          </Box>
        </Box>
      </Modal>
    </SoftBox>
  );
};

export default ExpertServices;